import { isCurved } from './curve.js';
import { cellKey, slotCountOf } from './grid.js';
/**
 * 点が多角形の内側にあるか(交差数判定)。
 *
 * **凸性を仮定しない。** 動画枠は 4 頂点だが、編集で自己交差しない限り
 * 凹形にもなりうる(F-08 は頂点数を固定するだけで凸性は保証しない)。
 *
 * 辺上の点は「内側」として扱う —— 枠の縁に落とした操作を失敗にしない。
 */
export function isInside(point, polygon) {
    if (polygon.length < 3)
        return false;
    const [x, y] = point;
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i, i += 1) {
        const a = polygon[i];
        const b = polygon[j];
        if (a === undefined || b === undefined)
            continue;
        const [ax, ay] = a;
        const [bx, by] = b;
        // 辺の上に乗っているか。乗っていれば即座に内側とする。
        if (isOnSegment(point, a, b))
            return true;
        // 水平線 y との交差を数える。
        const straddles = ay > y !== by > y;
        if (!straddles)
            continue;
        const crossingX = ax + ((y - ay) / (by - ay)) * (bx - ax);
        if (x < crossingX)
            inside = !inside;
    }
    return inside;
}
/** 点が線分上にあるか。 */
function isOnSegment(p, a, b) {
    const cross = (b[0] - a[0]) * (p[1] - a[1]) - (b[1] - a[1]) * (p[0] - a[0]);
    // 正規化座標なので、この許容量は 1920px 換算で約 0.02px。
    if (Math.abs(cross) > 1e-9)
        return false;
    const withinX = Math.min(a[0], b[0]) - 1e-9 <= p[0] && p[0] <= Math.max(a[0], b[0]) + 1e-9;
    const withinY = Math.min(a[1], b[1]) - 1e-9 <= p[1] && p[1] <= Math.max(a[1], b[1]) + 1e-9;
    return withinX && withinY;
}
/**
 * ドロップ位置から割り当て先の**セル**を決める(#152 で枠からセルへ)。
 *
 * 1. 位置を含む枠のうち **`order` が最大のもの**(手前にあるもの)。
 *    その中で**位置が入っているセル**へ
 * 2. どの枠にも入らなければ **空きセルを持つ枠のうち `order` が最小のもの**の
 *    最初の空きセル
 * 3. すべて埋まっていれば `order` が最小の枠のセル 0
 *
 * **2 が「とりあえず落とした」操作を失敗にしない**ための規則(#33)。
 * 枠が 1 つしかない場合は必ずそこへ行く。
 *
 * `resolveCell` は「点がその枠のどのセルか」を返す —— **ホモグラフィの逆写像が
 * 要るので描画コアが持つ知識**(ADR-0029)。ここに埋め込むと同じ計算が
 * 2 か所に生まれてずれる。平面・折れた枠では `null` を返したらセル 0 に落とす。
 *
 * **曲面の枠**(#361 / [ADR-0060](../../../docs/adr/0060-curved-surface.md) の決定 5)は**輪郭を見ず、`resolveCell` だけで決める** ——
 * セルを返せば当たり、`null` なら当たらない(手前の裏に隠れた・裏に回った・盤面の外)。曲面の当たり判定は
 * いちばん近い片で厳密に決まるので、見えている部分が 2 つに分かれる形(大きい弧の両端だけが見える)も、
 * 輪郭の内側で手前の裏に隠れた点も、そのまま正しく扱える。
 */
export function pickCell(point, frames, assigned, resolveCell, 
/**
 * **内外の判定に使う輪郭**(#339)。**省略なら 4 隅**(`quad`)—— 平面の枠はそれで正しい。
 *
 * 折れた枠は角が 4 隅の線から張り出す・へこむので、描画コアの `frameOutline` を渡す
 * (形はホモグラフィの知識なので描画コアが持つ —— ADR-0057 の決定 5。ここに埋め込むと同じ計算が 2 か所に生まれる)。
 * **曲面の枠には使わない**(上の説明)。
 */
outlineOf = (frame) => frame.quad) {
    if (frames.length === 0)
        return null;
    if (point !== null) {
        // **上の枠から見る**(`order` が大きいほど上。同じなら先に並んだほう)。
        // 曲面の枠は輪郭を見ない(当たり判定だけで決める —— 上の説明)。
        const hits = frames
            .filter((f) => isCurved(f) || isInside(point, outlineOf(f)))
            .sort((a, b) => b.order - a.order);
        for (const frame of hits) {
            const cellIndex = resolveCell(frame, point);
            /*
             * **曲面の枠は、当たり判定が `null` なら当たらない**(#361 / ADR-0060 の決定 5)。手前の裏に隠れたところでは
             * 素材が描かれず写真の実物が見えている —— 下の枠へ回し、どれにも当たらなければ外に落とした扱い。
             * **平面・折れた枠は今までどおり**、`null`(逆写像の丸めで縁からわずかに外れた)ならセル 0 に落とす。
             */
            if (cellIndex === null && isCurved(frame))
                continue;
            return { frameId: frame.id, cellIndex: cellIndex ?? 0 };
        }
    }
    const byOrder = [...frames].sort((a, b) => a.order - b.order);
    for (const frame of byOrder) {
        const free = firstFreeCell(frame, assigned);
        if (free !== null)
            return { frameId: frame.id, cellIndex: free };
    }
    // すべて埋まっている。**先頭の枠に上書きする** —— 落とした操作を失敗にしない。
    const first = byOrder[0];
    return first === undefined ? null : { frameId: first.id, cellIndex: 0 };
}
/** 枠の中で最初の空きセル。埋まっていれば `null`。 */
function firstFreeCell(frame, assigned) {
    // **入れ物の数で数える**(#339)。折れた枠はセル 0 が埋まっていれば空きなし ——
    // 分割の数で数えると、埋まっているのに「セル 1 が空き」に見えてセル 0 を上書きしてしまう。
    const total = slotCountOf(frame);
    for (let index = 0; index < total; index += 1) {
        if (!assigned.has(cellKey({ frameId: frame.id, cellIndex: index })))
            return index;
    }
    return null;
}
/**
 * ステージ内のピクセル座標を正規化座標に直す。
 *
 * 表示サイズが 0 のときは判定できないため `null`(未レイアウト)。
 */
export function toNormalized(position, size) {
    if (size.width <= 0 || size.height <= 0)
        return null;
    return [position.x / size.width, position.y / size.height];
}
/**
 * 枠を縁取る `<polygon>` の `points`(#124)。
 *
 * **正規化座標を表示ピクセルに開く。** ドロップ先の枠だけを光らせるのに使う
 * —— 画面全体を覆うと「どこに落とせばよいか」が分からない。
 *
 * **原点は表示矩形の左上。** 呼び出し側が SVG 自体を `offsetX` / `offsetY`
 * へ寄せる前提。
 */
export function outlinePoints(quad, size) {
    return quad.map(([x, y]) => `${String(x * size.width)},${String(y * size.height)}`).join(' ');
}
//# sourceMappingURL=quad.js.map